import { Link } from "react-router-dom";
import PageHeader from "@/components/PageHeader";
import { Reveal } from "@/components/Reveal";
import { Boxes, Percent, Truck, Headphones, ArrowRight } from "lucide-react";
import { GALLERY } from "@/data/catalog";
import QuoteForm from "@/components/QuoteForm";

const PERKS = [
  { Icon: Percent, t: "Wholesale Pricing", d: "The more you order, the more you save. Transparent slab-based bulk pricing." },
  { Icon: Boxes, t: "Mass Manufacturing", d: "Dedicated production lines that scale from thousands to millions of units." },
  { Icon: Truck, t: "Scheduled Dispatch", d: "Staggered deliveries aligned to your inventory and warehouse cycles." },
  { Icon: Headphones, t: "Account Manager", d: "A dedicated specialist to manage your recurring bulk requirements." },
];

const TIERS = [
  { qty: "1,000 – 5,000", note: "Startup & trial batches", disc: "Standard" },
  { qty: "5,000 – 25,000", note: "Growing brands", disc: "Up to 12% off" },
  { qty: "25,000+", note: "Enterprise & distributors", disc: "Best wholesale rates" },
];

export default function BulkOrders() {
  return (
    <div data-testid="bulk-page">
      <PageHeader overline="Bulk Orders" title="Wholesale packaging at manufacturing scale." subtitle="Cut costs and secure supply with high-volume bulk manufacturing, slab-based pricing and a dedicated account manager." />

      <section className="mx-auto max-w-[1400px] px-5 lg:px-10 py-10">
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {PERKS.map((p, i) => (
            <Reveal key={p.t} delay={(i % 4) * 0.06}>
              <div className="rounded-3xl border border-border bg-card p-7 h-full">
                <span className="grid place-items-center h-12 w-12 rounded-2xl bg-secondary text-royal"><p.Icon size={22} /></span>
                <h3 className="mt-5 font-heading font-bold text-foreground">{p.t}</h3>
                <p className="mt-1.5 text-sm text-muted-foreground">{p.d}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      <section className="mx-auto max-w-[1400px] px-5 lg:px-10 py-12">
        <Reveal><h2 className="font-heading text-3xl lg:text-4xl font-extrabold text-foreground mb-8">Volume pricing tiers</h2></Reveal>
        <div className="grid md:grid-cols-3 gap-5">
          {TIERS.map((t, i) => (
            <Reveal key={t.qty} delay={i * 0.08}>
              <div className={`rounded-3xl border p-8 h-full ${i === 1 ? "border-royal bg-navy text-white" : "border-border bg-card"}`}>
                <span className={`font-mono-accent text-sm ${i === 1 ? "text-emerald" : "text-muted-foreground"}`}>{t.disc}</span>
                <h3 className="mt-3 font-heading text-2xl font-extrabold">{t.qty}</h3>
                <p className={`mt-1 text-sm ${i === 1 ? "text-white/70" : "text-muted-foreground"}`}>units · {t.note}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      <section className="mx-auto max-w-[1400px] px-5 lg:px-10 py-12">
        <div className="rounded-4xl border border-border bg-card p-8 lg:p-12 grid lg:grid-cols-2 gap-10 items-start">
          <div>
            <span className="overline text-royal">Request bulk pricing</span>
            <h2 className="mt-4 font-heading text-3xl font-extrabold text-foreground">Tell us your volume.</h2>
            <p className="mt-3 text-muted-foreground">Share your product and quantity — we&rsquo;ll send a tailored wholesale quote within one business day.</p>
            <div className="mt-8 overflow-hidden rounded-3xl border border-border"><img src={GALLERY[0]} alt="Warehouse" className="w-full aspect-[4/3] object-cover" /></div>
          </div>
          <QuoteForm />
        </div>
      </section>
    </div>
  );
}
